import { ReactElement } from 'react';

import { AppIcon, useContainerQuery } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { UserProfileResponse } from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Box, Typography, Button, SxProps, Theme } from '@mui/material';

import { AssessmentChecklist } from './AssessmentChecklist';
import { ChecklistDataOverrides } from '../assessMyTrack.types';
import { useAssessmentChecklist } from '../hooks/useAssessmentChecklist';

const STRINGS = appStrings.modules.assessMyTrack;

type AssessmentChecklistReviewProps = Readonly<{
  profileData: UserProfileResponse | undefined;
  checklistDataOverrides?: ChecklistDataOverrides;
  handleAssessMyTrack: () => void;
  isLoading: boolean;
  allowReassess: boolean;
  onBack: () => void;
  hasError?: boolean;
}>;

export const AssessmentChecklistReview = ({
  profileData,
  checklistDataOverrides,
  handleAssessMyTrack,
  isLoading,
  allowReassess,
  onBack,
  hasError = false,
}: AssessmentChecklistReviewProps): ReactElement => {
  const [containerRef, isCompact] = useContainerQuery(600);
  const { isChecklistComplete } = useAssessmentChecklist(profileData, checklistDataOverrides);

  const buttonSize = isCompact ? 'md' : 'xl';
  const iconSize = isCompact ? '16px' : '20px';

  return (
    <>
      <Box sx={headerSx}>
        <Typography variant="text-lg-semibold">{STRINGS.checklistReview.title}</Typography>
        <Typography
          variant="text-sm-regular"
          color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
        >
          {STRINGS.checklistReview.description}
        </Typography>
      </Box>
      <Box ref={containerRef} sx={checklistsContainerSx}>
        <AssessmentChecklist
          profileData={profileData}
          checklistDataOverrides={checklistDataOverrides}
          title={STRINGS.assessmentChecklist.title}
          description={STRINGS.assessmentChecklist.description}
          variant={{
            type: 'required',
            handleAssessMyTrack,
            isLoading,
            allowReassess,
            hideAssessButton: true,
          }}
        />
        <Box sx={rightColumnSx}>
          <AssessmentChecklist
            profileData={profileData}
            checklistDataOverrides={checklistDataOverrides}
            title={STRINGS.goTheExtraMile.title}
            description={STRINGS.goTheExtraMile.description}
            variant={{ type: 'optional' }}
          />
          <Button
            hierarchy="Secondary gray"
            size={buttonSize}
            onClick={onBack}
            startIcon={<AppIcon iconName="ArrowLeft" diameter={iconSize} color="Gray/700" />}
            id="assess-track-back-to-chart-btn"
          >
            {STRINGS.checklistReview.backToChart}
          </Button>
          <Button
            hierarchy="Primary"
            size={buttonSize}
            onClick={handleAssessMyTrack}
            disabled={!isChecklistComplete || !allowReassess || isLoading}
            id="assess-track-reassess-button"
          >
            {STRINGS.assessmentChecklist.assessMyTrack}
          </Button>
          {hasError && (
            <Typography variant="text-sm-regular" sx={errorTextSx}>
              {STRINGS.assessmentChecklist.assessError}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

const headerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const checklistsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-2xl'],
  width: '100%',
  '& > *': {
    flex: '1 1 400px',
  },
});

const rightColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: backrs.spacing['spacing-xl'],
  columnGap: backrs.spacing['spacing-2xl'],
  minWidth: 0,
});

const errorTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
  textAlign: 'center',
});
